import { type GetProductPointsRulesResponseType } from "@jti/fe-common";
import type { MagentoStockStatus } from "@jti/magento-types";

import type {
  ICartItemError,
  ICategoryTree,
  IItemSelectedBundleOptionValue,
  IMoney,
  IProductDiscount,
  IProductPrice,
  IProductStockStatus,
  ISelectedBundleOptionValue,
  ISpecialOffer,
  ITierPrice,
} from "../types";

export type ProductNicotineStrength = {
  name: string;
  rating: number;
};

export type SelectedBundleOption = {
  id: string;
  label: string;
  type?: string;
  uid?: string;
  values?: SelectedBundleOptionValue[];
};

export type OrderItemSelectedBundleOptionProduct = {
  id?: string | number | null;
  is_discontinued?: boolean | null;
  stock_status?: IProductStockStatus | null;
  tooltip_info?: string | null;
  small_image?: {
    url?: string | null;
    label?: string | null;
  } | null;
};

export type OrderItemSelectedBundleOptionPresentationInfo = {
  initial_price?: number | null;
  presentation_price?: number | null;
  product?: OrderItemSelectedBundleOptionProduct | null;
};

export type OrderItemSelectedBundleOptionValue = Pick<
  IItemSelectedBundleOptionValue,
  "id" | "product_name" | "quantity"
> & {
  presentation_info?: OrderItemSelectedBundleOptionPresentationInfo | null;
  price?: IItemSelectedBundleOptionValue["price"];
  product_sku?: IItemSelectedBundleOptionValue["product_sku"];
  uid?: IItemSelectedBundleOptionValue["uid"];
};

export type SelectedBundleOptionValue = ISelectedBundleOptionValue | IItemSelectedBundleOptionValue;

export type Category = { name: string; uid: string; store_locator_name?: string };

export type ProductPriceRangeMoney = {
  currency?: string | null;
  value?: number | null;
};

export type ProductPriceRangeTax = {
  label?: string | null;
  amount?: ProductPriceRangeMoney | null;
};

export type ProductPriceRange = {
  maximum_price?: {
    discount?: {
      amount_off?: number | null;
      percent_off?: number | null;
    } | null;
    fixed_product_taxes?: Array<ProductPriceRangeTax | null> | null;
  } | null;
  minimum_price: {
    final_price: ProductPriceRangeMoney;
    regular_price: ProductPriceRangeMoney;
    fixed_product_taxes?: Array<ProductPriceRangeTax | null> | null;
  };
};

export type Product = {
  id?: string;
  name: string;
  subscription?: number;
  subscription_format?: string;
  carton_format?: string;
  has_registration_consent?: boolean;
  delivery_region_code?: string;
  url?: string;
  imageUrl: string;
  quantity: number;
  description: string;
  short_description?: string;
  status?: MagentoStockStatus;
  sku: string;
  hidden_sku?: boolean;
  parentGiftSku?: string;
  parentGiftId?: string;
  price_range?: ProductPriceRange;
  price: {
    currency: string;
    value: number;
    currencyCode?: string;
    regularPrice?: number;
  };
  prices?: {
    price: IMoney;
    row_total: IMoney;
    row_total_including_tax: IMoney;
    total_item_discount: IMoney;
  };
  flavor?: number;
  flavor_label?: string;
  subtitle?: string;
  product_disclaimer?: string | null;
  image?: {
    url: string;
    label: string;
  };
  color_label?: string;
  categories?: Category[];
  enable_order_quantity_limit: number;
  order_quantity_limit: number;
  points?: number;
  rules: GetProductPointsRulesResponseType[];
  quantityLimitReached?: boolean;
  free_gifts?: {
    allowed_qty: number;
    products: {
      is_eligible: boolean;
      eligibility_description: string;
      product: FreeGift;
    }[];
  };
  tradeIn?: boolean;
  type_id?: string;
  isGift: boolean;
  isGiftRemovable?: boolean;
  isDiscontinued?: boolean;
  bundle_options?: SelectedBundleOption[];
  tooltip_info?: string;
  product_type?: string;
  promotionalOffers?: ISpecialOffer[];
  errors?: ICartItemError[] | null;
  hasDevice?: boolean;
  hasConsent?: boolean;
  nicotineStrength?: ProductNicotineStrength;
};

export type ProductVariant = {
  product: Pick<Product, "sku" | "name" | "flavor" | "color_label" | "product_disclaimer"> & {
    color: number;
    stock_status: IProductStockStatus;
    price_range: {
      minimum_price: Pick<IProductPrice, "final_price" | "regular_price"> & {
        discount: Pick<IProductDiscount, "percent_off">;
      };
    };
    categories: Pick<ICategoryTree, "name">[];
  };
};

export type MagentoConfigurableProduct = Pick<Product, "name" | "sku" | "flavor"> & {
  stock_status: IProductStockStatus;
  price_tiers: Array<ITierPrice>;
  variants: ProductVariant[];
  price_range: {
    minimum_price: Pick<IProductPrice, "final_price" | "regular_price"> & {
      discount: Pick<IProductDiscount, "percent_off">;
    };
  };
  categories: Pick<ICategoryTree, "name">[];
  id: number;
};

export type PgsProduct = Omit<Product, "id" | "description" | "status" | "price"> & {
  options: string;
  itemPrice: number;
  totalPrice: number;
};

export type FreeGift = Pick<Product, "name" | "sku" | "image"> & { stock_status: MagentoStockStatus } & {
  tooltip_info: string;
  quantityAddedToCart: number;
};
